import {
	Form,
	Link,
	useLoaderData,
	useLocation,
	useNavigate,
} from '@remix-run/react'
import {
	User,
	Users,
	Users2,
	LogOut,
	Activity,
	Trophy,
	Dumbbell,
	Target,
	BarChart3,
	Calendar,
	Layers,
	TrendingUp,
	ClipboardList,
	MessageSquare,
} from 'lucide-react'

import { useRef } from 'react'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '#app/components/ui/sidebar'
import { Button } from './components/ui/button'
import { type RootLoaderData, type loader } from './root'
import logo from './routes/_marketing+/logos/momentumlogo.png'
import { ThemeSwitch } from './routes/resources+/theme-switch'
import { cn, getGroupImgSrc } from './utils/misc'
import { userHasRole, useUser } from './utils/user'

export function AppSidebar() {
	const data = useLoaderData<typeof loader>() as RootLoaderData
	const userRoles = data.user?.roles ?? []
	const formRef = useRef<HTMLFormElement>(null)
	const navigate = useNavigate()
	const sidebarContext = useSidebar()
	const appUser = useUser()

	// Respect role toggles - a role the user turned off is not considered active
	const disabledRoles = data.disabledRoles
	const userIsAdmin = userHasRole(appUser, 'admin')
	const userIsCoach = userHasRole(appUser, 'coach')

	// Effective role checks - respects disabled roles
	const isAdmin = userIsAdmin && !disabledRoles.includes('admin')
	const isCoach = userIsCoach && !disabledRoles.includes('coach')

	const user = data.user
	const groups = data.isMobile ? [] : (data.user?.groups ?? [])
	const ownGroups = data.isMobile ? [] : (data.user?.ownGroups ?? [])

	const groupItems = groups.map((group) => ({
		title: group.name,
		url: `/groups/${group.name}`,
		icon: (
			<img
				src={getGroupImgSrc(group.image?.id)}
				alt={group.name}
				className={cn('rounded-full object-cover', {
					'h-[24px] w-[24px]': sidebarContext.open,
					'h-[16px] w-[16px]': !sidebarContext.open,
				})}
			/>
		),
	}))
	const ownGroupItems = ownGroups.map((group) => ({
		title: group.name,
		// Link to coaching management for coaches/admins
		url:
			isCoach || isAdmin
				? `/coaching/groups/${group.name}`
				: `/groups/${group.name}`,
		icon: (
			<img
				src={getGroupImgSrc(group.image?.id)}
				alt={group.name}
				className={cn('rounded-full object-cover', {
					'h-[24px] w-[24px]': sidebarContext.open,
					'h-[16px] w-[16px]': !sidebarContext.open,
				})}
			/>
		),
	}))
	const adminItems = [
		{
			title: 'Dashboard',
			url: '/admin',
			icon: <Layers className="text-ink-fg-soft" />,
		},
	]

	const coachingItems = [
		{
			title: 'Dashboard',
			url: `/coaching`,
			icon: <ClipboardList className="text-ink-fg-soft" />,
		},
		{
			title: 'Calendar',
			url: `/coaching/calendar`,
			icon: <Calendar className="text-ink-fg-soft" />,
		},
		{
			title: 'Periodization',
			url: `/coaching/periodization`,
			icon: <TrendingUp className="text-ink-fg-soft" />,
		},
		{
			title: 'Plans',
			url: `/coaching/periodization/macrocycles`,
			icon: <Layers className="text-ink-fg-soft" />,
		},
		{
			title: 'Strategies',
			url: `/coaching/periodization/strategies`,
			icon: <Target className="text-ink-fg-soft" />,
		},
		{
			title: 'Clients',
			url: `/coaching/clients`,
			icon: <User className="text-ink-fg-soft" />,
		},
		{
			title: 'Manage Groups',
			url: `/coaching/groups`,
			icon: <Users2 className="text-ink-fg-soft" />,
		},
	]
	const userItems = [
		...(user
			? [
					{
						title: 'Messages',
						url: `/messages`,
						icon: <MessageSquare className="text-ink-fg-soft" />,
					},
					{
						title: 'Achievements',
						url: `/achievements`,
						icon: <Trophy className="text-ink-fg-soft" />,
					},
					{
						title: 'Groups',
						url: `/groups`,
						icon: <Users className="text-ink-fg-soft" />,
					},
					{
						title: 'Workouts',
						url: `/workouts`,
						icon: <Dumbbell className="text-ink-fg-soft" />,
					},
					{
						title: 'Calendar',
						url: `/calendar`,
						icon: <Calendar className="text-ink-fg-soft" />,
					},
					{
						title: 'Exercises',
						url: `/exercises`,
						icon: <BarChart3 className="text-ink-fg-soft" />,
					},
					{
						title: 'Biomarkers',
						url: `/biomarkers`,
						icon: <Activity className="text-ink-fg-soft" />,
					},
					{
						title: 'Periodization',
						url: `/periodization`,
						icon: <TrendingUp className="text-ink-fg-soft" />,
					},
					{
						title: 'Profile',
						url: `/periodization/fitness-profile`,
						icon: <User className="text-ink-fg-soft" />,
					},
				]
			: []),
	]

	function selectMenuItem(item: { url: string } & any) {
		sidebarContext.setOpenMobile(false)
		navigate(item.url)
	}

	const allItems = [
		...(isAdmin ? adminItems : []),
		...(isCoach ? coachingItems : []),
		...userItems,
	]

	const location = useLocation()
	const currentItem = allItems.find(
		(item) =>
			item.url === location.pathname ||
			location.pathname.startsWith(item.url + '/'),
	)

	return user ? (
		<Sidebar collapsible="offcanvas" variant="floating">
			<SidebarHeader>
				<Link to="/" className="flex items-center gap-2 px-2">
					<img
						src={logo}
						alt=""
						className={cn('rounded-full object-cover', {
							'h-8 w-8': sidebarContext.open,
							'h-6 w-6': !sidebarContext.open,
						})}
					/>
					{sidebarContext.open ? (
						<span className="font-display text-h5">Momentum</span>
					) : null}
				</Link>
				{sidebarContext.open ? (
					<div className="flex flex-wrap gap-1.5 px-2 pt-1 text-body-2xs">
						{disabledRoles.length > 0 ? (
							<span className="bg-ember/15 rounded-full px-2 py-0.5 font-medium text-ember">
								{[
									'user',
									...userRoles
										.filter(
											(role) =>
												role.name !== 'user' && !disabledRoles.includes(role.name),
										)
										.map((role) => role.name),
								].join(' + ')}
							</span>
						) : (
							userRoles
								.filter((role) => role.name !== 'user')
								.map((role) => (
									<span
										key={role.name}
										className="rounded-full border border-m-rule bg-ink-soft px-2 py-0.5 text-ink-fg-soft"
									>
										{role.name}
									</span>
								))
						)}
					</div>
				) : null}
			</SidebarHeader>
			<SidebarContent>
				{isAdmin && (
					<>
						<SidebarGroup>
							<SidebarGroupLabel className="text-eyebrow text-ink-fg-soft">
								<div className="flex flex-grow justify-center text-center">
									Admin
								</div>
							</SidebarGroupLabel>
							<SidebarGroupContent>
								<SidebarMenu>
									{adminItems.map((item) => (
										<SidebarMenuItem key={item.title}>
											<SidebarMenuButton asChild>
												<Button
													variant={
														item.url === currentItem?.url ? 'default' : 'ghost'
													}
													onClick={() => selectMenuItem(item)}
												>
													{item.icon}
													<span>{item.title}</span>
												</Button>
											</SidebarMenuButton>
										</SidebarMenuItem>
									))}
								</SidebarMenu>
							</SidebarGroupContent>
						</SidebarGroup>
					</>
				)}
				{isCoach && (
					<>
						<SidebarGroup>
							<SidebarGroupLabel className="text-eyebrow text-ink-fg-soft">
								<div className="flex flex-grow justify-center text-center">
									Coaching
								</div>
							</SidebarGroupLabel>
							<SidebarGroupContent>
								<SidebarMenu>
									{coachingItems.map((item) => (
										<SidebarMenuItem key={item.title}>
											<SidebarMenuButton asChild>
												<Button
													variant={
														item.url === currentItem?.url ? 'default' : 'ghost'
													}
													onClick={() => selectMenuItem(item)}
												>
													{item.icon}
													<span>{item.title}</span>
												</Button>
											</SidebarMenuButton>
										</SidebarMenuItem>
									))}
								</SidebarMenu>
							</SidebarGroupContent>
						</SidebarGroup>
					</>
				)}

				<SidebarGroup>
					<SidebarGroupLabel className="text-eyebrow text-ink-fg-soft">
						<div className="flex flex-grow justify-center text-center">
							Momentum
						</div>
					</SidebarGroupLabel>
					<SidebarGroupContent>
						<SidebarMenu>
							{userItems.map((item) => (
								<SidebarMenuItem key={item.title}>
									<SidebarMenuButton asChild>
										<Button
											variant={
												item.url === currentItem?.url ? 'default' : 'ghost'
											}
											onClick={() => selectMenuItem(item)}
										>
											{item.icon}
											<span>{item.title}</span>
										</Button>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							<>
								{groupItems.length > 0 && (
									<SidebarGroupLabel className="text-eyebrow text-ink-fg-soft">
										<div className="flex flex-grow justify-center text-center">
											Groups I'm In
										</div>
									</SidebarGroupLabel>
								)}
								{groupItems.map((item) => (
									<SidebarMenuItem key={item.title}>
										<SidebarMenuButton asChild>
											<Button
												variant={
													item.url === currentItem?.url ? 'default' : 'ghost'
												}
												onClick={() => selectMenuItem(item)}
											>
												{item.icon}
												<span>{item.title}</span>
											</Button>
										</SidebarMenuButton>
									</SidebarMenuItem>
								))}
							</>
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							<>
								{ownGroupItems.length > 0 && (
									<SidebarGroupLabel className="text-eyebrow text-ink-fg-soft">
										<div className="flex flex-grow justify-center text-center">
											Groups I Own
										</div>
									</SidebarGroupLabel>
								)}
								{ownGroupItems.map((item) => (
									<SidebarMenuItem key={item.title}>
										<SidebarMenuButton asChild>
											<Button
												variant={
													item.url === currentItem?.url ? 'default' : 'ghost'
												}
												onClick={() => selectMenuItem(item)}
											>
												{item.icon}
												<span>{item.title}</span>
											</Button>
										</SidebarMenuButton>
									</SidebarMenuItem>
								))}
							</>
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
				<SidebarGroup>
					<SidebarGroupLabel className="text-eyebrow text-ink-fg-soft">
						<div className="flex flex-grow justify-center text-center">
							Theme
						</div>
					</SidebarGroupLabel>
					<SidebarMenu>
						<SidebarMenuItem>
							<SidebarMenuButton asChild>
								<div className="flex items-center justify-center">
									<ThemeSwitch
										showLabel={sidebarContext.open}
										userPreference={data.requestInfo.userPrefs.theme}
									/>
								</div>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarGroup>
			</SidebarContent>
			<SidebarFooter>
				<Form action="/logout" method="POST" ref={formRef}>
					<div className="flex items-center justify-center">
						<button type="submit" className="flex items-center gap-2">
							<LogOut />
							{sidebarContext.open ? 'Logout' : null}
						</button>
					</div>
				</Form>
			</SidebarFooter>
		</Sidebar>
	) : null
}
